
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TermsConditions = () => {
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  
  const handleAccept = () => {
    if (!accepted) {
      toast.error("Please accept the terms", {
        description: "You must accept the terms and conditions to proceed"
      });
      return;
    }
    
    // Save acceptance to localStorage
    localStorage.setItem("termsAccepted", "true");
    
    toast.success("Terms accepted", {
      description: "Thank you for accepting our terms and conditions"
    });
    
    // Navigate back to home
    navigate("/");
  };
  
  return (
    <div className="container py-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button 
          variant="outline" 
          asChild
          className="mb-4"
        >
          <Link to="/about-us">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to About Us
          </Link>
        </Button>
      
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white mb-4">
            Terms and Conditions
          </h1>
          <p className="text-muted-foreground mb-8">
            Last updated: April 9, 2025
          </p>
        </motion.div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="bg-white dark:bg-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Terms and Conditions for Noor Ali Learning Center</CardTitle>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p>
              Please read these terms and conditions carefully before using our application.
            </p>
            
            <h3>1. Agreement to Terms</h3>
            <p>
              By accessing or using our application, you agree to be bound by these Terms and Conditions and 
              our Privacy Policy. If you disagree with any part of the terms, you may not access the application.
            </p>
            
            <h3>2. Use License</h3>
            <p>
              Permission is granted to temporarily use this application for personal, non-commercial transitory 
              viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul>
              <li>modify or copy the materials;</li>
              <li>use the materials for any commercial purpose, or for any public display (commercial or non-commercial);</li>
              <li>attempt to decompile or reverse engineer any software contained in the application;</li>
              <li>remove any copyright or other proprietary notations from the materials; or</li>
              <li>transfer the materials to another person or "mirror" the materials on any other server.</li>
            </ul>
            
            <h3>3. Disclaimer</h3>
            <p>
              The materials within this application are provided on an 'as is' basis. Noor Ali Learning Center 
              makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties 
              including, without limitation, implied warranties or conditions of merchantability, fitness for a 
              particular purpose, or non-infringement of intellectual property or other violation of rights.
            </p>
            
            <h3>4. Limitations</h3>
            <p>
              In no event shall Noor Ali Learning Center or its suppliers be liable for any damages (including, 
              without limitation, damages for loss of data or profit, or due to business interruption) arising 
              out of the use or inability to use the materials on Noor Ali Learning Center's application, even 
              if Noor Ali Learning Center or a Noor Ali Learning Center authorized representative has been 
              notified orally or in writing of the possibility of such damage.
            </p>
            
            <h3>5. Accuracy of Materials</h3>
            <p>
              The materials appearing in the Noor Ali Learning Center application could include technical, 
              typographical, or photographic errors. Noor Ali Learning Center does not warrant that any of the 
              materials on its application are accurate, complete or current. Noor Ali Learning Center may make 
              changes to the materials contained on its application at any time without notice.
            </p>
            
            <h3>6. Links</h3>
            <p>
              Noor Ali Learning Center has not reviewed all of the sites linked to its application and is not 
              responsible for the contents of any such linked site. The inclusion of any link does not imply 
              endorsement by Noor Ali Learning Center of the site. Use of any such linked website is at the 
              user's own risk.
            </p>
            
            <h3>7. Modifications</h3>
            <p>
              Noor Ali Learning Center may revise these terms of service for its application at any time without 
              notice. By using this application you are agreeing to be bound by the then current version of these 
              terms of service.
            </p>
            
            <h3>8. Governing Law</h3>
            <p>
              These terms and conditions are governed by and construed in accordance with the laws of Afghanistan 
              and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
            </p>
            
            <div className="my-8 p-4 bg-nalc-teal/10 rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms" 
                  checked={accepted} 
                  onCheckedChange={(checked) => setAccepted(checked === true)}
                />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I have read and agree to the terms and conditions
                </label>
              </div>
              
              <Button 
                onClick={handleAccept} 
                className="mt-4 bg-nalc-teal hover:bg-nalc-teal/90"
              >
                <Check className="mr-2 h-4 w-4" /> Accept Terms
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default TermsConditions;
